import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
    IconPricingOverview,
    IconPricingAnalysis,
    IconPricingRecords,
    IconPricingConnections,
} from '../../../components/web/icons/pricing-group-icons';
import { Container } from '../../../components/web/container/container';
import { WebPricingAction } from './web-pricing-action';
import {
    BillingPeriod,
    getBillingPeriodByLookupKey,
    getPremiumLookupKeyByBillingPeriod,
} from '../../../models/functions/billing-period';
import { cn } from '../../../utils/tailwind';
import { getPriceComparison, prices } from './pricing-data';
import { useStickyPanel } from '../../../hooks/use-sticky-panel';
import styles from './homepage-pricing.module.css';
import { useAvailableBillingPeriods } from '../../../providers/pricing-offer-provider';
import { useRootStore } from '../../../providers/root-store-provider';
import { HomepagePricingPrice } from './homepage-pricing-price';

const VatSwitch: React.FC<{ withVat: boolean; onChange: (value: boolean) => void }> = ({ withVat, onChange }) => {
    const { t } = useTranslation();
    return (
        <div
            role="group"
            aria-label={t('pages.homepage.pricingDesign.vatLabel')}
            className="inline-flex shrink-0 rounded-full border border-[#d9cdc5] p-[0.3rem]"
        >
            {[true, false].map((value) => (
                <button
                    key={String(value)}
                    type="button"
                    aria-pressed={withVat === value}
                    onClick={() => onChange(value)}
                    className={cn(
                        'rounded-full px-[1.2rem] py-[0.5rem] text-[1.2rem] font-medium text-web-copy transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-primary',
                        withVat === value ? 'bg-[#d9cdc5]/40' : 'hover:bg-[#d9cdc5]/20',
                    )}
                >
                    {t(`blocks.pricing.common.${value ? 'withVat' : 'withoutVat'}`)}
                </button>
            ))}
        </div>
    );
};

const SavingsBadge: React.FC<{ period: BillingPeriod; withVat: boolean }> = ({ period, withVat }) => {
    const { t, i18n } = useTranslation();
    return (
        <div className="pointer-events-none absolute z-[3] -top-[1.5rem] left-[1.6rem] right-[1.6rem] sm:left-[3.2rem] sm:right-[3.2rem]">
            {(['halfyearly', 'yearly'] as const).map((offerPeriod) => {
                const comparison = getPriceComparison(offerPeriod, withVat);
                const visible = period === offerPeriod && comparison.savings > 0;
                const amount = new Intl.NumberFormat(i18n.language === 'en' ? 'en-US' : 'cs-CZ', {
                    style: 'currency',
                    currency: 'CZK',
                    maximumFractionDigits: 0,
                }).format(comparison.savings);
                const hasFreeMonths = offerPeriod === 'yearly' && comparison.freeMonths >= 2;
                const badgeClass =
                    'inline-flex shrink-0 items-center gap-[0.4rem] whitespace-nowrap rounded-full px-[0.8rem] py-[0.6rem] text-[1.1rem] font-bold leading-[1.8rem] text-neutral-white min-[360px]:px-[1rem] min-[360px]:text-[1.2rem] sm:px-[1.4rem] sm:text-[1.3rem]';
                return (
                    <span
                        key={offerPeriod}
                        aria-hidden={!visible}
                        data-visible={visible}
                        className={cn(styles.savingsBadge, 'absolute left-0 inline-flex max-w-full gap-[0.6rem]')}
                    >
                        <span
                            className={cn(badgeClass, offerPeriod === 'yearly' ? 'bg-brand-primary' : 'bg-[#e65c00]')}
                        >
                            {hasFreeMonths && (
                                <svg
                                    aria-hidden="true"
                                    viewBox="0 0 16 16"
                                    className="h-[1.2rem] w-[1.2rem] shrink-0"
                                    fill="currentColor"
                                >
                                    <path d="m8 0 2.1 5.9L16 8l-5.9 2.1L8 16l-2.1-5.9L0 8l5.9-2.1Z" />
                                </svg>
                            )}
                            {hasFreeMonths
                                ? t('pages.homepage.pricingDesign.twoMonthsFree')
                                : t('pages.homepage.pricingDesign.savings', { amount })}
                        </span>
                        {hasFreeMonths && (
                            <span className={cn(badgeClass, 'bg-[#e65c00]')}>
                                {t('pages.homepage.pricingDesign.savings', { amount })}
                            </span>
                        )}
                    </span>
                );
            })}
        </div>
    );
};

const reactionParticles = {
    halfyearly: [
        { id: 'left', emoji: '👍' },
        { id: 'center', emoji: '👍' },
        { id: 'right', emoji: '👍' },
    ],
    yearly: [
        { id: 'left', emoji: '💜' },
        { id: 'center', emoji: '😍' },
        { id: 'right', emoji: '💜' },
        { id: 'far-left', emoji: '💜' },
        { id: 'far-right', emoji: '💜' },
    ],
};

const PricingReaction: React.FC<{ period: 'halfyearly' | 'yearly' }> = ({ period }) => (
    <div aria-hidden="true" className={styles.reaction}>
        {reactionParticles[period].map(({ id, emoji }) => (
            <span key={id} className={styles.reactionParticle}>
                {emoji}
            </span>
        ))}
    </div>
);

const featureGroups = [
    { key: 'overview', icon: IconPricingOverview, features: ['portfolios', 'trades', 'dashboard'] },
    { key: 'analysis', icon: IconPricingAnalysis, features: ['profitCompareWithIndex', 'insideValue', 'priceAlert'] },
    { key: 'records', icon: IconPricingRecords, features: ['importTrade', 'taxReport', 'dividends'] },
    { key: 'beyond', icon: IconPricingConnections, features: ['investown', 'personalAssets', 'leaderboard'] },
];

const CardsConcept: React.FC<React.PropsWithChildren<{ available: BillingPeriod[]; readOnly?: boolean }>> = ({
    available,
    children,
    readOnly = false,
}) => {
    const { t } = useTranslation();
    const { cookieService } = useRootStore();
    const [withVat, setWithVat] = React.useState(true);
    const [selected, setSelected] = React.useState<BillingPeriod>('yearly');
    const selectedPeriod = available.includes(selected) ? selected : available[0];
    React.useEffect(() => {
        const savedPeriod = getBillingPeriodByLookupKey(cookieService.getSignupLookupKey());
        if (savedPeriod) setSelected(savedPeriod);
    }, [cookieService]);
    const [reaction, setReaction] = React.useState<{ period: 'halfyearly' | 'yearly'; id: number } | null>(null);
    const reactionId = React.useRef(0);
    React.useEffect(() => {
        if (!reaction) return undefined;
        const timeout = window.setTimeout(() => setReaction(null), 1600);
        return () => window.clearTimeout(timeout);
    }, [reaction]);
    const cardRef = React.useRef<HTMLDivElement>(null);
    const switchRef = React.useRef<HTMLDivElement>(null);
    const vatSwitchRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        const card = cardRef.current;
        if (!card) return undefined;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        let frame = 0;
        let paused = false;
        const switchers = [switchRef.current, vatSwitchRef.current].filter(
            (element): element is HTMLDivElement => element !== null,
        );
        const isUsingSwitcher = () => switchers.some((element) => element.matches(':hover, :focus-within'));
        const pause = () => {
            window.cancelAnimationFrame(frame);
            if (paused) return;
            card.style.transform = window.getComputedStyle(card).transform;
            card.style.transition = 'none';
            paused = true;
        };
        const resume = () => {
            if (isUsingSwitcher()) return;
            card.style.removeProperty('transform');
            card.style.removeProperty('transition');
            paused = false;
        };
        const handleFocusOut = () => queueMicrotask(resume);
        const reset = () => {
            window.cancelAnimationFrame(frame);
            card.style.removeProperty('transform');
            card.style.removeProperty('transition');
            paused = false;
            card.style.removeProperty('--shadow-x');
            card.style.removeProperty('--shadow-y');
            card.style.removeProperty('--magnet-x');
            card.style.removeProperty('--magnet-y');
            delete card.dataset.magnetic;
        };
        const move = (event: PointerEvent) => {
            if (event.pointerType !== 'mouse' || reducedMotion.matches) return;
            if (isUsingSwitcher()) {
                pause();
                return;
            }
            if (paused) return;
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(() => {
                const bounds = (card.parentElement ?? card).getBoundingClientRect();
                if (
                    bounds.bottom <= 0 ||
                    bounds.top >= window.innerHeight ||
                    bounds.right <= 0 ||
                    bounds.left >= window.innerWidth
                ) {
                    reset();
                    return;
                }
                const centerX = bounds.left + bounds.width / 2;
                const centerY = bounds.top + bounds.height / 2;
                const dx = event.clientX - centerX;
                const dy = event.clientY - centerY;
                const x = Math.max(-1, Math.min(1, dx / Math.max(1, dx < 0 ? centerX : window.innerWidth - centerX)));
                const y = Math.max(-1, Math.min(1, dy / Math.max(1, dy < 0 ? centerY : window.innerHeight - centerY)));
                card.style.setProperty('--shadow-x', `${-x * 110}px`);
                card.style.setProperty('--shadow-y', `${72 - y * 65}px`);
                const inside =
                    event.clientX >= bounds.left &&
                    event.clientX <= bounds.right &&
                    event.clientY >= bounds.top &&
                    event.clientY <= bounds.bottom;
                if (inside) {
                    card.dataset.magnetic = 'true';
                    card.style.setProperty('--magnet-x', `${(dx / (bounds.width / 2)) * 18}px`);
                    card.style.setProperty('--magnet-y', `${(dy / (bounds.height / 2)) * 12}px`);
                } else {
                    delete card.dataset.magnetic;
                    card.style.removeProperty('--magnet-x');
                    card.style.removeProperty('--magnet-y');
                }
            });
        };
        switchers.forEach((switcher) => {
            switcher.addEventListener('pointerenter', pause);
            switcher.addEventListener('pointerleave', resume);
            switcher.addEventListener('focusin', pause);
            switcher.addEventListener('focusout', handleFocusOut);
        });
        window.addEventListener('pointermove', move, { passive: true });
        window.addEventListener('blur', reset);
        window.addEventListener('scroll', reset, { passive: true });
        document.documentElement.addEventListener('pointerleave', reset);
        reducedMotion.addEventListener('change', reset);
        return () => {
            reset();
            switchers.forEach((switcher) => {
                switcher.removeEventListener('pointerenter', pause);
                switcher.removeEventListener('pointerleave', resume);
                switcher.removeEventListener('focusin', pause);
                switcher.removeEventListener('focusout', handleFocusOut);
            });
            window.removeEventListener('pointermove', move);
            window.removeEventListener('blur', reset);
            window.removeEventListener('scroll', reset);
            document.documentElement.removeEventListener('pointerleave', reset);
            reducedMotion.removeEventListener('change', reset);
        };
    }, [available.length]);

    return (
        <div
            className={cn(
                'grid items-center gap-[4.8rem] vl:grid-cols-[1.3fr_1fr] vl:gap-[6rem]',
                Boolean(children) && 'items-start',
            )}
        >
            <div>
                <h2
                    id="pricing-heading"
                    className="mb-[2.4rem] text-[2.4rem] font-bold leading-[1.25] text-brand-secondary"
                >
                    {t('pages.homepage.pricingDesign.included')}
                </h2>
                <div className="grid gap-x-[3.2rem] gap-y-[2.4rem] sm:grid-cols-2">
                    {featureGroups.map((group) => (
                        <div key={group.key} className="pt-[1.4rem]">
                            <div className="mb-[1.2rem] flex min-h-[3.2rem] items-center gap-[1.2rem] [&_svg]:h-[3.2rem] [&_svg]:w-[3.2rem]">
                                <group.icon />
                                <h4 className="text-[1.6rem] font-bold text-brand-secondary leading-[1.3]">
                                    {t(`pages.homepage.pricingConcepts.groups.${group.key}`)}
                                </h4>
                            </div>
                            <ul className="space-y-[0.6rem] pl-[4.4rem] text-[1.6rem] leading-relaxed text-web-copy">
                                {group.features.map((key) => (
                                    <li key={key} className="flex items-start gap-[0.8rem]">
                                        <svg
                                            aria-hidden="true"
                                            viewBox="0 0 12 12"
                                            fill="none"
                                            className="mt-[0.7rem] h-[1.2rem] w-[1.2rem] shrink-0 text-brand-primary"
                                        >
                                            <path
                                                d="m2.5 6 2.25 2.25 4.75-4.5"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                        {t(`blocks.pricing.premium.features.${key}`)}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
            <div
                className={cn(
                    'min-w-0',
                    Boolean(children) &&
                        'vl:sticky vl:top-[12rem] vl:col-start-2 vl:row-start-1 vl:row-span-2 vl:self-start',
                )}
            >
                <div
                    className={cn(
                        styles.card,
                        'min-w-0 rounded-[2.4rem] border border-[#d9cdc5] p-[2.4rem] sm:p-[3.2rem]',
                    )}
                    ref={cardRef}
                >
                    <SavingsBadge period={selectedPeriod} withVat={withVat} />
                    {reaction && <PricingReaction key={reaction.id} period={reaction.period} />}
                    <div className="mb-[3.2rem] flex flex-wrap items-center justify-between gap-[1.6rem]">
                        <h3 className="text-[2.4rem] font-bold text-brand-secondary leading-[1.3]">Premium</h3>
                        {available.length > 1 ? (
                            <div
                                ref={switchRef}
                                role="group"
                                aria-label={t('pages.homepage.pricingConcepts.periodLabel')}
                                className="inline-grid max-w-full grid-cols-3 gap-[0.2rem] rounded-full border border-[#d9cdc5] p-[0.3rem]"
                            >
                                {available.map((period) => (
                                    <button
                                        key={period}
                                        type="button"
                                        aria-pressed={selectedPeriod === period}
                                        onClick={() => {
                                            if (!readOnly) {
                                                cookieService.selectPreferredSubscription(
                                                    getPremiumLookupKeyByBillingPeriod(period),
                                                );
                                            }
                                            if (period === selectedPeriod) return;
                                            setSelected(period);
                                            if (
                                                period === 'monthly' ||
                                                window.matchMedia('(prefers-reduced-motion: reduce)').matches
                                            ) {
                                                setReaction(null);
                                            } else {
                                                reactionId.current += 1;
                                                setReaction({ period, id: reactionId.current });
                                            }
                                        }}
                                        className={cn(
                                            'min-w-0 rounded-full px-[1.4rem] py-[0.7rem] text-[1.2rem] font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-primary',
                                            selectedPeriod === period
                                                ? 'bg-[#d9cdc5]/40 text-web-copy'
                                                : 'text-web-copy hover:bg-[#d9cdc5]/40',
                                        )}
                                    >
                                        {t(`pages.homepage.pricingConcepts.periods.${period}`)}
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <p className="text-[1.5rem] text-web-copy">
                                {t(`blocks.pricing.common.${prices[selectedPeriod].label}`)}
                            </p>
                        )}
                    </div>
                    <div aria-live="polite" aria-atomic="true">
                        <HomepagePricingPrice period={selectedPeriod} withVat={withVat} />
                    </div>
                    <div className="my-[2.4rem] flex items-center gap-[1.6rem]">
                        <span aria-hidden="true" className="h-px min-w-0 flex-1 bg-[#d9cdc5]" />
                        <div ref={vatSwitchRef} className="inline-flex shrink-0">
                            <VatSwitch withVat={withVat} onChange={setWithVat} />
                        </div>
                        <span aria-hidden="true" className="h-px min-w-0 flex-1 bg-[#d9cdc5]" />
                    </div>
                    <div>
                        <p
                            className="mb-[2.4rem] text-[1.5rem] leading-relaxed text-web-copy"
                            aria-live="polite"
                            aria-atomic="true"
                        >
                            <Trans
                                i18nKey={`pages.homepage.pricingDesign.trialFeatures.${selectedPeriod}`}
                                components={{ strong: <strong className="font-bold text-brand-secondary" /> }}
                            />
                        </p>
                        {!readOnly && <WebPricingAction period={selectedPeriod} />}
                        <p className="mt-[1.6rem] text-[1.3rem] leading-relaxed text-web-copy">
                            {t('pages.homepage.pricingDesign.trialTerms')}
                        </p>
                    </div>
                </div>
            </div>
            {Boolean(children) && <div className="min-w-0 vl:col-start-1 vl:row-start-2">{children}</div>}
        </div>
    );
};

export const HomepagePricing: React.FC<React.PropsWithChildren<{ readOnly?: boolean }>> = ({
    children,
    readOnly = false,
}) => {
    const panelRef = useStickyPanel();
    const periods = useAvailableBillingPeriods();
    return (
        <section
            id="pricing"
            data-standalone-pricing={!children || undefined}
            aria-labelledby="pricing-heading"
            className={cn('scroll-mt-[10.4rem] py-[8rem] lg:py-[12rem] xxl:py-[16rem]', !children && styles.standalone)}
        >
            <div ref={panelRef}>
                <Container>
                    <CardsConcept available={periods} readOnly={readOnly}>
                        {children}
                    </CardsConcept>
                </Container>
            </div>
        </section>
    );
};
